import _ from "lodash";
import { defineStore } from "pinia";
import { useTalkRoomStore } from "./TalkRoomStore";
import { useObserverStore } from "./ObserverStore";
import type { ITalkRoomRepository, talkUserDataObserverType } from "~/interfaces/ITalkRoomRepository";
import { TalkRoomRepository } from "~/repositories/TalkRoomRepository";
import type { TalkUserData } from "~/entities/TalkUserData";
import type { Talk } from "~/entities/Talk";

/**
 * トークユーザーデータを扱うクラス
 * （ブックマーク・メンションリスト）
 */
export const useTalkUserDataStore = defineStore("TalkUserDataStore", () => {
  const selectedPlanId = ref<string | undefined>(); // 選択中の講座ID

  // トークユーザーデータリスト
  const talkThreads = ref<TalkUserData[]>([]);
  const talkBookmarks = ref<TalkUserData[]>([]);
  const talkMentions = ref<TalkUserData[]>([]);

  // スレッド・ブックマークをすべて取得済みかどうかフラグ
  // スレッド・ブックマークデータの監視範囲決定に必要
  // （メンションは時系列で常に最新取得となるので不要）
  const hasFetchedAllThreads = ref<boolean>(false);
  const hasFetchedAllBookmarks = ref<boolean>(false);

  const talkRoomStore = useTalkRoomStore();
  const observerStore = useObserverStore();

  const repository: ITalkRoomRepository = new TalkRoomRepository();

  // 講座初期化
  const initPlan = (planId: string) => {
    selectedPlanId.value = planId;
    talkBookmarks.value = [];
    talkMentions.value = [];
    hasFetchedAllThreads.value = false;
    hasFetchedAllBookmarks.value = false;
  };

  // 選択中講座 ID 取得
  const getSelectedPlanId = () => selectedPlanId.value;

  // スレッドをすべて取得済みかどうか取得
  const getHasFetchedAllThreads = () => hasFetchedAllThreads.value;

  // ブックマークをすべて取得済みかどうか取得
  const getHasFetchedAllBookmarks = () => hasFetchedAllBookmarks.value;

  // トークユーザーデータ監視関数型
  type TalkUserDataObserveFunc = (
    planId: string,
    userId: string,
    callback: talkUserDataObserverType,
    oldestTalkId: number,
  ) => void;

  // トークユーザーデータ監視コールバック取得（ブックマーク・メンションでロジック共有）
  const getTalkUserDataListObserveCallback = (
    planId: string,
    userId: string,
    talkUserDataListRef: Ref<TalkUserData[]>,
    startObserve: TalkUserDataObserveFunc,
  ) => {
    const addCallback = (addedData: TalkUserData) => {
      if (talkUserDataListRef.value.some(data => data.talk.talkId === addedData.talk.talkId)) {
        // 既に保持しているデータは無視（でないと無限ループしてしまう）
        return;
      }
      const addedList = [...talkUserDataListRef.value, addedData];
      talkUserDataListRef.value = _.sortBy(addedList, data => data.createdAt).reverse();
      startTalkUserDataListObserve(planId, userId, talkUserDataListRef, startObserve); // 監視範囲を更新
    };
    const modifyCallback = (modifiedData: TalkUserData) => {
      // ブックマーク・メンションでは modify は発生しない
      // スレッドでは最新コメントの日時が createdAt として更新される場合があるので、下記処理を行う
      const indexToModify = talkUserDataListRef.value.findIndex(data => data.talk.talkId === modifiedData.talk.talkId);
      if (indexToModify === -1) {
        // 見つからない場合は無視（既に削除されているなど）
        return;
      }
      talkUserDataListRef.value[indexToModify] = modifiedData; // 参照を更新
      // createdAt 降順でソートし、最新投稿が先頭に来るようにする
      talkUserDataListRef.value.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      // 監視範囲は更新不要
    };
    const removeCallback = (removedData: TalkUserData) => {
      const indexToRemove = talkUserDataListRef.value.findIndex(data => data.talk.talkId === removedData.talk.talkId);
      if (indexToRemove === -1) {
        // 見つからない場合は無視（既に削除されているなど）
        return;
      }
      talkUserDataListRef.value.splice(indexToRemove, 1);
      startTalkUserDataListObserve(planId, userId, talkUserDataListRef, startObserve); // 監視範囲を更新
    };
    const callback = (userData: TalkUserData, type: "added" | "modified" | "removed") => {
      if (type === "added") {
        addCallback(userData);
      } else if (type === "modified") {
        modifyCallback(userData);
      } else if (type === "removed") {
        removeCallback(userData);
      } else {
        sentryErrorLog(`getTalkUserDataListObserveCallback: unexpected change type: ${type}`);
      }
    };
    return callback;
  };

  // トークユーザーデータ監視開始（ブックマーク・メンションでロジック共有）
  const startTalkUserDataListObserve = (
    planId: string,
    userId: string,
    talkUserDataListRef: Ref<TalkUserData[]>,
    startObserve: TalkUserDataObserveFunc,
  ) => {
    // 末尾は最も古いローカルデータの talkId
    const len = talkUserDataListRef.value.length;
    const oldestTalkId = len ? Number(talkUserDataListRef.value[len - 1].talk.talkId) : 0; // データなしの場合は 0 で全監視するので OK

    // 監視用コールバック取得
    const callback = getTalkUserDataListObserveCallback(planId, userId, talkUserDataListRef, startObserve);

    // 監視開始
    startObserve(planId, userId, callback, oldestTalkId);
  };

  // スレッドデータ監視開始
  const startTalkThreadsObserve = (planId: string, userId: string) => {
    observerStore.add("talkThreads", {
      start: () =>
        startTalkUserDataListObserve(
          planId,
          userId,
          talkThreads,
          (planId: string, userId: string, callback: talkUserDataObserverType, oldestTalkId: number) =>
            repository.startTalkThreadsObserve(planId, userId, callback, hasFetchedAllThreads.value ? 0 : oldestTalkId),
        ),
      stop: () => repository.stopTalkThreadsObserve(),
    });
  };

  // スレッドデータ監視停止
  const stopTalkThreadsObserve = () => {
    observerStore.remove("talkThreads");
  };

  // ブックマークデータ監視開始
  const startTalkBookmarksObserve = (planId: string, userId: string) => {
    // ブックマークの場合、すべて fetch 済みであれば oldestTalkId を 0 にして全体監視する
    // そうすることで古いトークをブックマーク追加したときにもリストへ反映される
    observerStore.add("talkBookmarks", {
      start: () =>
        startTalkUserDataListObserve(
          planId,
          userId,
          talkBookmarks,
          (planId: string, userId: string, callback: talkUserDataObserverType, oldestTalkId: number) =>
            repository.startTalkBookmarksObserve(
              planId,
              userId,
              callback,
              hasFetchedAllBookmarks.value ? 0 : oldestTalkId,
            ),
        ),
      stop: () => repository.stopTalkBookmarksObserve(),
    });
  };

  // ブックマークデータ監視停止
  const stopTalkBookmarksObserve = () => {
    observerStore.remove("talkBookmarks");
  };

  // メンションデータ監視開始
  const startTalkMentionsObserve = (planId: string, userId: string) => {
    observerStore.add("talkMentions", {
      start: () =>
        startTalkUserDataListObserve(
          planId,
          userId,
          talkMentions,
          (planId: string, userId: string, callback: talkUserDataObserverType, oldestTalkId: number) =>
            repository.startTalkMentionsObserve(planId, userId, callback, oldestTalkId),
        ),
      stop: () => repository.stopTalkMentionsObserve(),
    });
  };

  // メンションデータ監視停止
  const stopTalkMentionsObserve = () => {
    observerStore.remove("talkMentions");
  };

  // トークユーザーデータフェッチ関数型
  type TalkUserDataFetchFunc = (
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
  ) => Promise<TalkUserData[]>;

  // トークユーザーデータリスト取得
  const fetchTalkUserDataList = async (
    planId: string,
    userId: string,
    lastCreatedAt: Date | undefined,
    count: number,
    talkUserDataListRef: Ref<TalkUserData[]>,
    fetchFunc: TalkUserDataFetchFunc,
    updateObserveFunc: (planId: string, userId: string) => void,
  ): Promise<number> => {
    const fetchedList = await fetchFunc(planId, userId, lastCreatedAt, count);
    await talkRoomStore.updateTalkRoomUsers(
      planId,
      fetchedList.map(f => f.talk),
    );
    talkUserDataListRef.value = talkUserDataListRef.value.concat(fetchedList);
    updateObserveFunc(planId, userId); // 監視範囲を更新
    return fetchedList.length; // 追加された個数を返す
  };

  // トークスレッドリスト取得
  const fetchTalkThreads = async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
    const fetchedCount = await fetchTalkUserDataList(
      planId,
      userId,
      lastCreatedAt,
      count,
      talkThreads,
      async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
        return await repository.getTalkThreads(planId, userId, lastCreatedAt, count);
      },
      startTalkThreadsObserve,
    );
    if (fetchedCount < count) {
      // 最大数取得できなかった場合はすべて読み込み済みと判断し、
      // 監視範囲を広げて古いスレッドの追加も検知できるようにする
      hasFetchedAllThreads.value = true;
      startTalkThreadsObserve(planId, userId);
    }
    return fetchedCount;
  };

  // トークブックマークリスト取得
  const fetchTalkBookmarks = async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
    const fetchedCount = await fetchTalkUserDataList(
      planId,
      userId,
      lastCreatedAt,
      count,
      talkBookmarks,
      async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
        return await repository.getTalkBookmarks(planId, userId, lastCreatedAt, count);
      },
      startTalkBookmarksObserve,
    );
    if (fetchedCount < count) {
      // 最大数取得できなかった場合はすべて読み込み済みと判断し、
      // 監視範囲を広げて古いブックマークの追加も検知できるようにする
      hasFetchedAllBookmarks.value = true;
      startTalkBookmarksObserve(planId, userId);
    }
    return fetchedCount;
  };

  // トークメンションリスト取得
  const fetchTalkMentions = async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
    return await fetchTalkUserDataList(
      planId,
      userId,
      lastCreatedAt,
      count,
      talkMentions,
      async (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => {
        return await repository.getTalkMentions(planId, userId, lastCreatedAt, count);
      },
      startTalkMentionsObserve,
    );
  };

  // トークユーザーデータリストから選択
  const selectTalkUserData = async (userData: TalkUserData, talkUserDataListRef: Ref<TalkUserData[]>) => {
    const talk = userData.talk;
    const talkRoomId = talk.talkRoomId; // ユーザーデータのトークルームはトーク自体から取得する
    if (!talkRoomId) {
      throw new Error("selectTalkUserData: talkRoomId is not found");
    }

    // トーク削除されていたらユーザーデータリストから除去して NG 終了
    const latestTalk = await repository.getTalk(selectedPlanId.value!, talkRoomId, talk.talkId);
    if (!latestTalk || latestTalk.isDeleted()) {
      const index = talkUserDataListRef.value.findIndex(data => data.talk.talkId === talk.talkId);
      talkUserDataListRef.value.splice(index, 1);
      return false;
    }

    // トーク内容が変わっているかもしれないのでユーザーデータリストのトークを最新に更新
    const targetData = talkUserDataListRef.value.find(data => data.talk.talkId === talk.talkId);
    if (!targetData) {
      throw new Error("selectTalkUserData: targetData is not found");
    }
    targetData.talk = latestTalk;

    // ユーザーデータリスト内の同ルームに属するデータをすべて既読にする（このあと、そこへ飛ぶため）
    talkUserDataListRef.value
      .filter(data => data.talk.talkRoomId === talkRoomId)
      .forEach(data => {
        data.hasUnread = false;
      });

    return true;
  };

  // トークスレッドリストから選択
  const selectTalkThread = async (thread: TalkUserData) => {
    return await selectTalkUserData(thread, talkThreads);
  };

  // トークブックマークリストから選択
  const selectTalkBookmark = async (bookmark: TalkUserData) => {
    return await selectTalkUserData(bookmark, talkBookmarks);
  };

  // トークメンションリストから選択
  const selectTalkMention = async (mention: TalkUserData) => {
    return await selectTalkUserData(mention, talkMentions);
  };

  // トークブックマーク設定
  const setBookmark = async (userId: string, planId: string, talkRoomId: string, talkId: string, value: boolean) => {
    // ブックマーク設定を保存
    const result = await repository.setBookmark(userId, planId, talkRoomId, talkId, value);
    if (!result) {
      // 個数制限オーバー
      return false;
    }

    // 表示反映
    talkRoomStore.updateTalkValue(talkId, (talk: Talk) => {
      talk.bookmarked = value;
    });

    return true;
  };

  return {
    talkThreads,
    talkBookmarks,
    talkMentions,

    initPlan,

    startTalkThreadsObserve, // VisibleForTesting: start は fetch と addCallBack で実施するので本来公開不要
    startTalkBookmarksObserve, // VisibleForTesting: start は fetch と addCallBack で実施するので本来公開不要
    startTalkMentionsObserve, // VisibleForTesting: start は fetch と addCallBack で実施するので本来公開不要
    stopTalkThreadsObserve,
    stopTalkBookmarksObserve,
    stopTalkMentionsObserve,

    fetchTalkThreads,
    fetchTalkBookmarks,
    fetchTalkMentions,

    selectTalkThread,
    selectTalkBookmark,
    selectTalkMention,

    setBookmark,

    // VisibleForTesting
    getTalkUserDataListObserveCallback,
    getSelectedPlanId,
    getHasFetchedAllThreads,
    getHasFetchedAllBookmarks,
  };
});
